import { Tag } from '@carbon/react';
import { format0 } from 'utils/number';
import type { ScenarioChoice } from './types';
import styles from './case-editor.module.scss';

/**
 * The areas a scenario covers, as tags: "Applies to all areas" when the API says so (Price Deck,
 * or a catch-all DCA), each area name, "and N more" past the cap, and "No areas" for a scenario
 * of a coverage domain with none. An area domain with none covers no slot; a Type Curve with none
 * gives no permit, DUC or LTD a forecast (PDPs are still valued).
 *
 * @param {object} props - The scenario's tag fields and its domain's kind.
 * @param {ScenarioChoice} props.scenario - The scenario.
 * @param {boolean} props.gatesCoverage - Whether its domain decides which slots a run values.
 * @return {JSX.Element} The tags.
 */
export function AreaTags({
  scenario,
  gatesCoverage,
}: {
  scenario: Pick<
    ScenarioChoice,
    'areaTags' | 'areaTagsMoreCount' | 'areaTagsAppliesToAll'
  >;
  gatesCoverage: boolean;
}) {
  const noAreas =
    gatesCoverage &&
    !scenario.areaTagsAppliesToAll &&
    scenario.areaTags.length === 0;

  return (
    <span className={styles.tags}>
      {scenario.areaTagsAppliesToAll && (
        <Tag size="sm" type="green">
          Applies to all areas
        </Tag>
      )}
      {scenario.areaTags.map((name) => (
        <Tag key={name} size="sm" type="cool-gray">
          {name}
        </Tag>
      ))}
      {scenario.areaTagsMoreCount > 0 && (
        <Tag size="sm" type="outline">
          and {format0(scenario.areaTagsMoreCount)} more
        </Tag>
      )}
      {noAreas && (
        <Tag size="sm" type="red">
          No areas
        </Tag>
      )}
    </span>
  );
}
